// Answers "does this key fit my car?" on the product page.
//
// blocks/product-fitment-check.liquid renders all three answers hidden, plus the
// product's fitment set as JSON. This picks the right one. Doing it client-side
// is forced: the customer's car lives in localStorage (written by
// assets/vehicle-drawer.js), which Liquid can't read.

const ACTIVE_VEHICLE_KEY = 'tapsy:active-vehicle';

/** @returns {{brand: string, brandSlug: string, model: string, modelSlug: string, year: string}|null} */
function activeVehicle() {
  try {
    const vehicle = JSON.parse(localStorage.getItem(ACTIVE_VEHICLE_KEY) || 'null');
    return vehicle?.brandSlug && vehicle?.modelSlug ? vehicle : null;
  } catch {
    return null;
  }
}

function vehicleLabel(vehicle) {
  return `${vehicle.brand} ${vehicle.model}${vehicle.year ? ` ${vehicle.year}` : ''}`.trim();
}

/**
 * A fitment matches when brand and model agree and the vehicle's year sits in
 * its range. Year 0 means the dashboard left the bound open, so it doesn't
 * constrain the match.
 */
function matches(fitments, vehicle) {
  const year = parseInt(vehicle.year, 10);

  return fitments.some((fitment) => {
    if (fitment.brand !== vehicle.brandSlug || fitment.model !== vehicle.modelSlug) return false;
    if (!Number.isFinite(year)) return true;

    const from = Number(fitment.from) || 0;
    const to = Number(fitment.to) || 0;
    if (from && year < from) return false;
    if (to && year > to) return false;
    return true;
  });
}

class ProductFitmentCheck extends HTMLElement {
  connectedCallback() {
    let fitments = [];
    try {
      fitments = JSON.parse(this.querySelector('[data-fitment-data]')?.textContent || '[]');
    } catch {
      return;
    }
    if (!Array.isArray(fitments) || fitments.length === 0) return;

    const vehicle = activeVehicle();
    const state = !vehicle ? 'none' : matches(fitments, vehicle) ? 'fits' : 'no-fit';
    this.#show(state, vehicle);

    // The customer can change car without leaving the page.
    window.addEventListener('storage', (event) => {
      if (event.key === ACTIVE_VEHICLE_KEY) this.connectedCallback();
    });
  }

  #show(state, vehicle) {
    for (const panel of this.querySelectorAll('[data-fitment-state]')) {
      panel.hidden = panel.dataset.fitmentState !== state;
    }

    const panel = this.querySelector(`[data-fitment-state="${state}"]`);
    if (!panel) return;

    if (vehicle) {
      const message = panel.querySelector('[data-fitment-message]');
      if (message) message.textContent = message.textContent.replace('[vehicle]', vehicleLabel(vehicle));

      // Same URL the vehicle drawer builds, so the breadcrumb on the far side
      // picks the car up as the parent crumb.
      const link = panel.querySelector('[data-fitment-link]');
      if (link) {
        const tag = `caryear:${vehicle.brandSlug}-${vehicle.modelSlug}-${vehicle.year}`;
        const base = this.dataset.resultsBase || '/collections/all';
        link.href = `${base}?filter.p.tag=${encodeURIComponent(tag)}`;
      }
    }

    // Reuse the header's drawer rather than duplicating the picker.
    const prompt = panel.querySelector('[data-fitment-select]');
    if (prompt && !prompt.dataset.bound) {
      prompt.dataset.bound = 'true';
      prompt.addEventListener('click', () => {
        document.querySelector('[data-vehicle-trigger]')?.click();
      });
    }
  }
}

if (!customElements.get('product-fitment-check')) {
  customElements.define('product-fitment-check', ProductFitmentCheck);
}
